import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { environment } from 'src/environments/environment';
import { currentTenant } from '../core/tenant.interceptor';

/**
 * The tenant's logo before any PD session exists (the /auth/start landing page). Once a session
 * loads, its own presigned logo takes over; this only fills the gap before that and covers links
 * whose token has not been opened yet.
 */
@Injectable({ providedIn: 'root' })
export class PublicBrandingService {
  private readonly http = inject(HttpClient);
  private requestedFor: string | null = null;

  readonly logo = signal<string | null>(null);

  /** Settles once the lookup has answered (or failed, or there is no tenant) so a loader can wait for it briefly. */
  readonly ready: Promise<void>;
  private markReady: () => void = () => undefined;

  constructor() {
    this.ready = new Promise<void>((resolve) => { this.markReady = resolve; });
  }

  /** One lookup per tenant per page load; any failure just leaves the default logo. */
  load(): void {
    const tenant = currentTenant();
    if (!tenant) {
      this.markReady();
      return;
    }
    if (tenant === this.requestedFor) return;
    this.requestedFor = tenant;
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    this.http
      .get<{ data?: { logo?: string | null } }>(`${base}/branding/logo`, { params: { subdomain: tenant } })
      .subscribe({
        next: (res) => {
          this.logo.set(res?.data?.logo?.trim() ?? null);
          this.markReady();
        },
        error: () => {
          this.logo.set(null);
          this.markReady();
        },
      });
  }
}
